import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Popover, Typography } from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useApolloClient, useQuery } from '@apollo/client';
import { ALERTS, READ_ALERTS, TEST_ALERT, refreshDeviceAlerts, enableDeviceAlerts, disableDeviceAlerts, syncAppBadge } from '../../util/deviceAlerts';
import { INSTALL_CHANGED, isAppInstalled } from '../../../apps/timely/installPrompt';

const notificationPermission = () => 'Notification' in window ? Notification.permission : 'unavailable';

export default function NotificationBadge({ email }) {
  const client = useApolloClient();
  const { data, error, refetch } = useQuery(ALERTS, { fetchPolicy: 'network-only', pollInterval: 30000 });
  const [anchorEl, setAnchorEl] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [permission, setPermission] = useState(notificationPermission);
  const [invite, setInvite] = useState(false);
  const dismissed = useRef(false);
  const button = useRef(null);
  const alerts = data?.myAlerts;
  const unread = alerts?.unread;
  useEffect(() => { if (typeof unread === 'number') syncAppBadge(unread); }, [unread]);
  useEffect(() => {
    const offer = () => {
      let deferred = dismissed.current;
      try { deferred = deferred || sessionStorage.getItem(`timely-alerts-dismissed:${email}`) === 'true'; } catch { /* Storage is optional. */ }
      setInvite(Boolean(alerts?.pushConfigured && !deferred && isAppInstalled() && notificationPermission() === 'default'));
    };
    offer();
    window.addEventListener(INSTALL_CHANGED, offer);
    return () => window.removeEventListener(INSTALL_CHANGED, offer);
  }, [alerts?.pushConfigured, email, permission]);
  useEffect(() => {
    refreshDeviceAlerts(client).then(setEnabled).catch(() => setEnabled(false));
    if (new URLSearchParams(window.location.search).get('alerts') === '1') setAnchorEl(button.current);
    const refresh = () => {
      if (!document.hidden) {
        setPermission(notificationPermission());
        refreshDeviceAlerts(client).then(setEnabled).catch(() => setEnabled(false));
        refetch();
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [email, refetch, client]);
  const action = async (callback, refreshBadge = false) => {
    setBusy(true); setFeedback('');
    try {
      await callback();
      const result = await refetch();
      if (refreshBadge && result.data?.myAlerts) await syncAppBadge(result.data.myAlerts.unread);
    }
    catch (failure) { setFeedback(failure.message || 'Could not update alerts. Please try again.'); setAnchorEl(button.current); }
    finally { setPermission(notificationPermission()); setBusy(false); }
  };
  const dismissInvite = () => {
    dismissed.current = true;
    try { sessionStorage.setItem(`timely-alerts-dismissed:${email}`, 'true'); } catch { /* Storage is optional. */ }
    setInvite(false);
  };
  const enable = () => action(async () => {
    await enableDeviceAlerts(client, alerts.publicKey);
    setEnabled(true);
    dismissInvite();
    setFeedback('Notifications enabled on this device.');
  }, true);
  const read = id => action(() => client.mutate({ mutation: READ_ALERTS, variables: { id } }));
  return <>
    <Dialog open={invite} onClose={busy ? undefined : dismissInvite} aria-labelledby="timely-alert-invite-title">
      <DialogTitle id="timely-alert-invite-title">Allow Timely365 notifications?</DialogTitle>
      <DialogContent>
        <Typography>Get new alerts when Timely365 is closed and see unread counts on supported app icons.</Typography>
        {feedback && <Alert severity="warning" sx={{ mt: 2 }}>{feedback}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={dismissInvite}>Not now</Button>
        <Button disabled={busy} onClick={enable}>Allow notifications</Button>
      </DialogActions>
    </Dialog>
    <IconButton ref={button} color="inherit" aria-label={`Notifications, ${alerts?.unread || 0} unread`} onClick={event => setAnchorEl(event.currentTarget)}>
      <Badge badgeContent={alerts?.unread || 0} color="error"><NotificationsIcon /></Badge>
    </IconButton>
    <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
      <Box sx={{ p: 2, width: 340, maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box', maxHeight: '75vh', overflow: 'auto' }}>
        <Typography variant="subtitle1">Notifications</Typography>
        <Typography variant="body2" color="text.secondary">Get alerts on this device, even when Timely365 is closed. Supported app icons also show your unread count.</Typography>
        {permission === 'denied' && <Alert severity="warning">Notifications are blocked. Allow notifications in your browser or device settings, then return here to connect this device.</Alert>}
        {permission === 'unavailable' && <Alert severity="info">On iPhone or iPad, open Timely365 from your Home Screen to allow notifications. Otherwise, use a browser that supports device notifications.</Alert>}
        <Button disabled={busy || (!enabled && (!alerts?.pushConfigured || permission === 'denied' || permission === 'unavailable'))} onClick={() => enabled ? action(async () => {
          await disableDeviceAlerts(client);
          setEnabled(false);
        }) : enable()}>{enabled ? 'Turn off device alerts' : permission === 'granted' ? 'Enable device alerts' : 'Allow notifications on this device'}</Button>
        {enabled && <Button disabled={busy} onClick={() => action(() => client.mutate({ mutation: TEST_ALERT }))}>Send test alert</Button>}
        {alerts && !alerts.pushConfigured && <Typography variant="caption">Device alerts are not configured on this server yet.</Typography>}
        {(feedback || error) && <Alert severity="warning">{feedback || 'Could not load alerts. Please try again.'}</Alert>}
        {!!alerts?.unread && <Button disabled={busy} onClick={() => read(null)}>Mark all read</Button>}
        {alerts?.items.map(item => <Box key={item.id} sx={{ borderTop: '1px solid', borderColor: 'divider', py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: item.readAt ? 400 : 600 }}>{item.title}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{item.message}</Typography>
          {!item.readAt && <Button size="small" disabled={busy} onClick={() => read(item.id)}>Mark read</Button>}
        </Box>)}
        {alerts && !alerts.unread && <Typography sx={{ mt: 1 }} variant="body2">No unread notifications</Typography>}
      </Box>
    </Popover>
  </>;
}
